'use strict';
import isObject from 'lodash/isObject';
import {Observable} from 'rxjs/Observable';
import 'rxjs/add/observable/from';
import 'rxjs/add/observable/of';
import 'rxjs/add/operator/delay';
import 'rxjs/add/operator/throttleTime';
import {getId} from '../../../core-utilities/domain/ReduxUtils';
import {epicErrorHandler} from '../../../core-utilities/rx-utils';
import {storage} from '../../../config/constants';
import {firebaseAuthSuccess} from '../../../auth/actions';
import {
  addToLastAccessedAssets,
  addToLastAccessedOrganizations,
  addToLastAccessedProjects,
  assignAssetToModel,
  assignOrganizationToModel,
  assignProjectToModel,
  assignUserToModel,
  initLastAccessedAssets,
  initLastAccessedOrganizations,
  initLastAccessedProjects,
  removeFromLastAccessedAssets,
  removeFromLastAccessedOrganizations,
  removeFromLastAccessedProjects,
  selectedAsset,
  selectedOrganization,
  selectedProject,
} from '../actions';
import {getValidCache, getValidOrgCache, initialOrgChildState, initialOrgState} from '../reducers/lastAccessed';
import {logger} from './logger';

const CACHE_THROTTLE = 100;
const USER_PREFS_CACHE = 'REDUX_userPrefs';

export const epics = [
  onLastAccessedChanged,
  onLoginHydrateUserPrefs,
  onHydrateUserPrefsSelectOrganization,
  onHydrateUserPrefsSelectProject,
  onHydrateUserPrefsSelectAsset,
];

const inject = [
  'UserDomain',
  'UserPreferenceDomain',
];

onHydrateUserPrefsSelectProject.$inject = inject;
onHydrateUserPrefsSelectAsset.$inject = inject;
onHydrateUserPrefsSelectOrganization.$inject = inject;

/**
 * Caches latest state in localStorage
 * Epic does not emit any actions.
 * @param {ActionsObservable<ReduxAction>} action$
 * @param {Store} store
 * @return {Observable.<ReduxAction>}
 */
export function onLastAccessedChanged(action$, store) {
  return action$.ofType(firebaseAuthSuccess.TYPE)
    .map(action => action.payload.userId)
    .filter(id => !!id)
    .switchMap(userId =>
      Observable.merge(
        action$.ofType(addToLastAccessedOrganizations.TYPE),
        action$.ofType(addToLastAccessedProjects.TYPE),
        action$.ofType(addToLastAccessedAssets.TYPE),
        action$.ofType(removeFromLastAccessedOrganizations.TYPE),
        action$.ofType(removeFromLastAccessedProjects.TYPE),
        action$.ofType(removeFromLastAccessedAssets.TYPE),
      )
        .throttleTime(CACHE_THROTTLE)
        .delay(CACHE_THROTTLE / 10)
        .map(() => {
          const userPrefs = store.getState().userPrefs;
          storage.set(USER_PREFS_CACHE, {
            ...userPrefs,
            userId,
          });
        })
        .filter(() => false)
    )
    .catch(epicErrorHandler(logger, 'onLastAccessedChanged'));
}

/**
 * @param {ActionsObservable<ReduxAction>} action$
 * @return {Observable.<ReduxAction>}
 */
export function onLoginHydrateUserPrefs(action$) {
  return action$.ofType(firebaseAuthSuccess.TYPE)
    .map(action => action.payload.userId)
    .filter(id => !!id)
    .distinctUntilChanged()
    .switchMap(userId => {
      const cached = storage.get(USER_PREFS_CACHE);
      if (isObject(cached) && cached.userId === userId) {
        return Observable.from([
          initLastAccessedOrganizations(getValidOrgCache(cached.lastAccessedOrganizations)),
          initLastAccessedProjects(getValidCache(cached.lastAccessedProjects)),
          initLastAccessedAssets(getValidCache(cached.lastAccessedAssets)),
        ]);
      } else {
        return Observable.from([
          initLastAccessedOrganizations(initialOrgState()),
          initLastAccessedProjects(initialOrgChildState()),
          initLastAccessedAssets(initialOrgChildState()),
        ]);
      }
    })
    .filter((actions) => actions !== null)
    .catch(epicErrorHandler(logger, 'onLoginHydrateUserPrefs'));
}

export function onHydrateUserPrefsSelectProject(action$, store, dependencies) {
  return buildHydrateSelector(
    action$,
    store,
    dependencies,
    'onHydrateUserPrefsSelectProject',
    'project',
    initLastAccessedProjects.TYPE,
    assignProjectToModel.TYPE,
    selectedProject,
  );
}

export function onHydrateUserPrefsSelectAsset(action$, store, dependencies) {
  return buildHydrateSelector(
    action$,
    store,
    dependencies,
    'asset',
    'onHydrateUserPrefsSelectAsset',
    initLastAccessedAssets.TYPE,
    assignAssetToModel.TYPE,
    selectedAsset,
  );
}

export function onHydrateUserPrefsSelectOrganization(action$, store, dependencies) {
  return Observable.combineLatest(
    action$.ofType(assignUserToModel.TYPE)
      .map(action => getId(action.payload.user))
      .filter(id => !!id),
    action$.ofType(initLastAccessedOrganizations.TYPE)
      .takeUntil(action$.ofType(assignOrganizationToModel.TYPE)),
    (userId, action) => ({userId, action})
  )
    .take(1)
    .switchMap(({userId, action}) => {
      const cached = action.payload.state;
      if (cached && cached[0] && cached[0].id) {
        return Observable.of(selectedOrganization(cached[0].id));
      } else {
        return dependencies.UserPreferenceDomain.getLastOrgId(userId)
          .then(orgId => orgId ? selectedOrganization(orgId) : null);
      }
    })
    .filter((actions) => actions !== null)
    .catch(epicErrorHandler(logger, 'onHydrateUserPrefsSelectOrganization'));
}

function buildHydrateSelector(action$, store, dependencies, name, prop, initType, assignType, selectAction) {
  return Observable.combineLatest(
    action$.ofType(assignUserToModel.TYPE)
      .map(action => getId(action.payload.user))
      .filter(id => !!id)
      .take(1),
    action$.ofType(initType)
      .take(1),
    action$.ofType(assignOrganizationToModel.TYPE)
      .map(action => getId(action.payload.organization))
      .filter(id => !!id),
    (userId, action, orgId) => ({userId, orgId, action})
  )
    .switchMap(({userId, orgId, action}) => {
      const cached = action.payload.state[orgId];
      if (cached && cached[0] && cached[0].id) {
        return Observable.of(selectAction(cached[0].id))
          .takeUntil(action$.ofType(assignType));
      } else {
        // attempt to retrieve data from firebase
        return Observable.fromPromise(dependencies.UserDomain.$getAppStateRelationRef(userId, orgId)
          .child(prop)
          .once('value')
          .then((ss) => selectAction(ss.exists() ? ss.val() : null))
          .catch((err) => selectAction(null)))
          .takeUntil(action$.ofType(assignType));
      }
    })
    .filter((actions) => actions !== null)
    .catch(epicErrorHandler(logger, name));
}



// WEBPACK FOOTER //
// ./src/js/app/model/epics/lastAccessed.js